import React from 'react';
import cn from 'classnames';
import { useSnackbar } from 'notistack';
import defaultLocales from 'globals/locales/default';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import LockOpenOutlinedIcon from '@material-ui/icons/LockOpenOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import {
  Box,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import FsStatusLabel from 'ui-elements/common-elements/FsStatusLabel';
import FsDate from 'ui-elements/common-elements/FsDate';
import FsAvatarWithName from 'ui-elements/common-elements/FsAvatarWithName';
import FsIconButton from 'ui-elements/buttons/FsIconButton';
import { isLastElement, wait } from 'globals/utils';
import FsConfirmationDialog from 'ui-elements/modals/FsConfirmationDialog';
import FsBody from 'ui-elements/typography/FsBody';
import FsBodyBold from 'ui-elements/typography/FsBodyBold';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsDefaultDialog from 'ui-elements/modals/FsDefaultDialog';
import FsButton from 'ui-elements/buttons/FsButton';
import { TeammateStatus, UserRole } from 'globals/types';
import {
  HeadCell,
  HeadLabel,
  Cell,
  Row,
  Role,
  BodyLabel,
} from './FsUsersManagementTable.style-elements';
import { getColor, useStyles } from './FsUsersManagementTable.style';
import UserEditForm from './UserEditForm';

export interface FsUsersManagementSingleRowProps {
  id: string | number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: UserRole;
  statusType: TeammateStatus;
  date: number;
  invitedBy?: string;
}

export interface FsInvitationsTableProps {
  rows?: FsUsersManagementSingleRowProps[];
  headLabels?: string[];
}

const FsUsersManagementTable: React.FC<FsInvitationsTableProps> = ({
  rows = [],
  headLabels = [],
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { icon: iconClass } = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement>();
  const [currentId, setCurrentId] = React.useState<number | string>();
  const currentRowProps = rows.find(({ id }) => id === currentId);
  const currentUser = `${currentRowProps?.firstName} ${currentRowProps?.lastName}`;

  const [showDeleteDialog, setShowDeleteDialog] = React.useState<boolean>(
    false
  );
  const [showEditDialog, setShowEditDialog] = React.useState<boolean>(false);
  const [
    showResetPasswordDialog,
    setShowResetPasswordDialog,
  ] = React.useState<boolean>(false);
  const [pending, setPending] = React.useState<boolean>(false);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isUpLg = useMediaQuery(theme.breakpoints.up('lg'), {
    noSsr: true,
  });
  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const handleIconButtonClick = (
    event: React.MouseEvent<HTMLElement>,
    id: number | string
  ): void => {
    setAnchorEl(event.currentTarget);
    setCurrentId(id);
  };

  const handleClose = (): void => {
    setAnchorEl(undefined);
  };

  const handleDeletionItemClick = (): void => {
    handleClose();
    setShowDeleteDialog(true);
  };

  const handleResetPasswordItemClick = (): void => {
    handleClose();
    setShowResetPasswordDialog(true);
  };

  const handleEditItemClick = (): void => {
    handleClose();
    setShowEditDialog(true);
  };

  const handleDeletionAction = async (): Promise<void> => {
    setPending(true);
    // eslint-disable-next-line no-console
    console.log('Remove user', currentId);
    await wait(1000);
    setPending(false);
    setShowDeleteDialog(false);
    enqueueSnackbar(defaultLocales.removeUserNotificationSuccess, {
      variant: 'success',
    });
  };

  const handleResetPasswordAction = async (): Promise<void> => {
    setPending(true);
    // eslint-disable-next-line no-console
    console.log('Reset password for item', currentId);
    await wait(1000);
    setPending(false);
    setShowResetPasswordDialog(false);
    enqueueSnackbar(defaultLocales.resetPasswordNotificationSuccess, {
      variant: 'success',
    });
  };

  return (
    <Box>
      <TableContainer style={{ overflow: 'visible' }}>
        <Table>
          {headLabels.length ? (
            <TableHead>
              <TableRow data-test="table-head-row">
                {headLabels.map((label) => (
                  <HeadCell key={label}>
                    <HeadLabel>{label}</HeadLabel>
                  </HeadCell>
                ))}
              </TableRow>
            </TableHead>
          ) : null}
          <TableBody>
            {rows.map(
              (
                { id, firstName, lastName, statusType, role, date, email },
                idx
              ) => (
                <Row
                  key={id}
                  className={cn({ isLast: isLastElement(idx, rows?.length) })}
                >
                  <Cell component="th" width={isUpLg ? '45%' : '55%'}>
                    <FsAvatarWithName subContent={email}>
                      {!isUpLg && (
                        <Role component="strong">
                          {defaultLocales.enums[role]}
                        </Role>
                      )}
                      {firstName} {lastName}{' '}
                    </FsAvatarWithName>
                  </Cell>
                  {isUpLg && (
                    <Cell width="10%">
                      <Role>{defaultLocales.enums[role]}</Role>
                    </Cell>
                  )}
                  {isUpLg ? (
                    <>
                      <Cell width="15%">
                        <FsDate date={date} withYear withTime />
                      </Cell>
                      <Cell width="15%">
                        <FsDate date={date} withYear />
                      </Cell>
                    </>
                  ) : (
                    <Cell width="25%">
                      <BodyLabel>{defaultLocales.lastActivity}</BodyLabel>
                      <FsDate date={date} withYear withTime />
                      <Box marginTop={1}>
                        <BodyLabel>{defaultLocales.createdAt}</BodyLabel>
                        <FsDate date={date} withYear />
                      </Box>
                    </Cell>
                  )}

                  <Cell width="15%">
                    <FsStatusLabel bgColor={getColor(statusType)}>
                      {defaultLocales.enums[statusType]}
                    </FsStatusLabel>
                  </Cell>
                  <Cell withIconOnly>
                    <FsIconButton
                      size="small"
                      onClick={(e) => handleIconButtonClick(e, id)}
                    >
                      <MoreVertIcon />
                    </FsIconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={open && currentId === id}
                      onClose={handleClose}
                    >
                      <MenuItem onClick={handleEditItemClick}>
                        <EditOutlinedIcon className={iconClass} />
                        <span>{defaultLocales.edit}</span>
                      </MenuItem>
                      <MenuItem onClick={handleResetPasswordItemClick}>
                        <LockOpenOutlinedIcon className={iconClass} />
                        <span>{defaultLocales.resetPassword}</span>
                      </MenuItem>
                      <MenuItem onClick={handleDeletionItemClick}>
                        <DeleteOutlineOutlinedIcon className={iconClass} />
                        <span>{defaultLocales.removeUser}</span>
                      </MenuItem>
                    </Menu>
                  </Cell>
                </Row>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modals */}

      <FsConfirmationDialog
        title={defaultLocales.removeUser}
        showModal={showDeleteDialog}
        setShowModal={setShowDeleteDialog}
        actionButtons={[
          <FsButton key={1} onClick={() => setShowDeleteDialog(false)}>
            {defaultLocales.no}
          </FsButton>,
          <FsPendingButton
            key={2}
            pending={pending}
            onClick={handleDeletionAction}
            startIcon={<DeleteOutlineOutlinedIcon />}
          >
            {isUpSm
              ? `${defaultLocales.yes}, ${defaultLocales.remove}`
              : defaultLocales.remove}
          </FsPendingButton>,
        ]}
      >
        <FsBody>
          {defaultLocales.removeUserQuestion}{' '}
          <FsBodyBold component="span">{currentUser}</FsBodyBold>?
        </FsBody>
      </FsConfirmationDialog>

      <FsConfirmationDialog
        title={defaultLocales.resetPassword}
        showModal={showResetPasswordDialog}
        setShowModal={setShowResetPasswordDialog}
        actionButtons={[
          <FsButton key={1} onClick={() => setShowResetPasswordDialog(false)}>
            {defaultLocales.no}
          </FsButton>,
          <FsPendingButton
            key={2}
            pending={pending}
            onClick={handleResetPasswordAction}
            startIcon={<LockOpenIcon />}
          >
            {isUpSm
              ? `${defaultLocales.yes}, ${defaultLocales.resetPassword}`
              : defaultLocales.reset}
          </FsPendingButton>,
        ]}
      >
        <FsBody>
          {defaultLocales.resetPasswordQuestionLong}{' '}
          <FsBodyBold component="span">{currentUser}</FsBodyBold>?
        </FsBody>
      </FsConfirmationDialog>

      <FsDefaultDialog
        disableBackdropClick
        disableEscapeKeyDown
        title={defaultLocales.editTeammate}
        isOpen={showEditDialog}
        fullWidth
        maxWidth="sm"
        onClose={() => setShowEditDialog(false)}
      >
        <UserEditForm
          {...currentRowProps}
          onClose={() => setShowEditDialog(false)}
        />
      </FsDefaultDialog>
    </Box>
  );
};

export default FsUsersManagementTable;
